import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { fetchPlatformOverview } from '../../home/platform';

/** The cluster's free reservation, for the impact line of the panel. */
export interface ClusterMargin {
  cpu: number;
  memory: number;
}

/**
 * The cluster's free reservation (allocatable minus requested), from the
 * admin « Plateforme » card's route (`/overview` of `waadoo-platform`): for
 * the admin tier alone — anyone else gets `{ admin: false }` and no margin.
 * Millicores and Mi; undefined while asked, for a non-admin, on a failure.
 */
export function useClusterMargin(asked: boolean): ClusterMargin | undefined {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [margin, setMargin] = useState<ClusterMargin>();
  useEffect(() => {
    if (!asked) return undefined;
    let active = true;
    const load = async () => {
      try {
        const overview = await fetchPlatformOverview({
          fetchApi,
          discoveryApi,
        });
        if (active && overview.admin && overview.cluster) {
          const { cluster } = overview;
          setMargin({
            cpu: cluster.cpuAllocatableMillis - cluster.cpuRequestedMillis,
            memory:
              (cluster.memAllocatableBytes - cluster.memRequestedBytes) /
              1024 ** 2,
          });
        }
      } catch {
        // No margin: the impact line says the environment's own figures.
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [asked, fetchApi, discoveryApi]);
  return margin;
}

/** A signed figure: « +1,2 Gio », « −0,1 CPU ». */
export function signed(
  value: number,
  format: (value: number) => string,
): string {
  return `${value < 0 ? '−' : '+'}${format(Math.abs(value))}`;
}
